import { Component } from '@theme/component';

/** JSON-LD block the Klaviyo "Product reviews" widget publishes into <head>. */
const RICH_SNIPPET_ID = 'kl_reviews_rich_snippet';

/** The rating scale both the metafield and the rich snippet use. */
const RATING_SCALE = 5;

/** Reviews containers to fall back to, best first, when the configured anchor is missing. */
const REVIEWS_FALLBACK_IDS = ['klaviyo-product-reviews-wrapper', 'klaviyo-reviews-all', 'reviewslist'];

/**
 * @typedef {object} StarRatingRefs
 * @property {HTMLAnchorElement} [link] - The rating, linked to the reviews section.
 * @property {HTMLElement} [average] - The "(4.7)" average.
 * @property {HTMLElement} [reviewCount] - The "19 reviews" count.
 * @property {HTMLElement} [placeholder] - Editor-only note shown while there is no rating.
 */

/**
 * Displays a product's review average next to the price.
 *
 * The only rating Liquid can see is the `reviews.rating` metafield, and on this
 * store that value is maintained by hand: whole product families share one
 * average, some products carry a rating with a review count of zero, and the
 * newer lines have no metafield at all. The Klaviyo "Product reviews" block
 * publishes the true per-product aggregate as JSON-LD, but only after its script
 * runs — so the metafield is rendered as a seed and corrected here the moment the
 * real numbers land. Templates still on the old "All reviews" block publish no
 * snippet (that widget is store-wide, not per-product) and simply keep the seed.
 *
 * @extends {Component<StarRatingRefs>}
 */
class StarRating extends Component {
  /** @type {MutationObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    if (this.#render()) return;

    // Klaviyo appends the snippet to <head> once its bundle has fetched the reviews.
    this.#observer = new MutationObserver(() => {
      if (this.#render()) this.#observer?.disconnect();
    });
    this.#observer.observe(document.head, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  /**
   * Scrolls to the reviews list.
   *
   * The anchor saved on the block goes stale easily: templates are mid-migration
   * between the two Klaviyo blocks, which use different wrapper ids, and both
   * wrappers are injected client-side after this block renders. So honour the
   * configured target when it resolves, and find the reviews list ourselves when
   * it doesn't, rather than leaving a link that goes nowhere.
   *
   * @param {MouseEvent} event - The click on the rating.
   */
  scrollToReviews = (event) => {
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;

    const href = this.refs.link?.getAttribute('href') ?? '';
    if (href.startsWith('#') && document.getElementById(href.slice(1))) return;

    const fallback = REVIEWS_FALLBACK_IDS.map((id) => document.getElementById(id)).find(Boolean);
    if (!fallback) return;

    event.preventDefault();
    fallback.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /**
   * The real aggregate for this product, if the reviews widget has published it.
   * @returns {{rating: number, count: number} | null} The aggregate, or null.
   */
  get #aggregate() {
    const snippet = document.getElementById(RICH_SNIPPET_ID);
    if (!snippet?.textContent) return null;

    let data;
    try {
      data = JSON.parse(snippet.textContent);
    } catch {
      return null;
    }

    // The snippet is page-level, so check it is describing this block's product.
    const { productUrl } = this.dataset;
    const id = data['@id'];
    if (productUrl && typeof id === 'string' && !id.startsWith(productUrl)) return null;

    const rating = Number(data.aggregateRating?.ratingValue);
    const count = Number(data.aggregateRating?.reviewCount);
    if (!(rating > 0) || !(count > 0)) return null;

    return { rating, count };
  }

  /**
   * Swaps the seeded rating for the real one.
   * @returns {boolean} Whether a real aggregate was available to render.
   */
  #render() {
    const aggregate = this.#aggregate;
    if (!aggregate) return false;

    const { rating, count } = aggregate;
    const { link, average, reviewCount, placeholder } = this.refs;
    const label = this.dataset.label ?? '';
    const rounded = rating.toFixed(1);

    this.style.setProperty('--srating-pct', `${(rating / RATING_SCALE) * 100}%`);
    if (average) average.textContent = `(${rounded})`;
    if (reviewCount) reviewCount.textContent = `${count} ${label}`.trim();

    if (link) {
      link.setAttribute(
        'aria-label',
        `${rounded} out of ${RATING_SCALE} stars, ${count} ${label}. See all reviews.`.replace('  ', ' ')
      );
      link.hidden = false;
    }

    placeholder?.remove();

    return true;
  }
}

if (!customElements.get('star-rating')) {
  customElements.define('star-rating', StarRating);
}
